<script setup lang="ts">
import type { ItemInfo } from "@/types"
import type { MatchTab } from "@/types/"
import { KButton } from "@kaleidoscope-solutions/ks-design-system"
import { RefreshIcon } from "@kaleidoscope-solutions/ks-icons/vue/light"
import { computedAsync, useCloned } from "@vueuse/core"
import { storeToRefs } from "pinia"
import { computed, ref } from "vue"
import MatchTable from "@/components/FileImport/Match/MatchTable.vue"
import { useFileImportStore } from "@/stores/"
import { Decorator, ItemMatchStatus } from "@/types"
import { useSnackbar } from "@/utils/useSnackbar"

interface Props {
  /**
   * Tab Items
   */
  tab: MatchTab
}

const props = defineProps<Props>()

const { addSnackbar } = useSnackbar()

const fileImportStore = useFileImportStore()

const categoryName = computed(() => props.tab.title)

const { itemMatchInfo } = storeToRefs(fileImportStore)

const unmatchedItems = computed(() => {
  const items: ItemInfo[] = itemMatchInfo.value.get(categoryName.value) ?? []

  return items // retain indices
    .map((itemInfo, index) => ({ itemInfo, index }))
    .filter((info) => info.itemInfo.status === ItemMatchStatus.PENDING)
})

const unmatchedItemsCount = computed(() => unmatchedItems.value.length)

const tabContainsUnmatchedItems = computed(() => unmatchedItemsCount.value > 0)

const matchedItems = computed(
  () => itemMatchInfo.value.get(categoryName.value) ?? []
)

const categoryItems = computedAsync(
  async () => await fileImportStore.getCategoryItems(categoryName.value),
  []
)

const createdItems = computed(() =>
  fileImportStore.getCreatedItems(categoryName.value)
)

const dropDownOptions = computedAsync(
  () => ([...categoryItems.value, ...createdItems.value].sort()),
  []
)

const snapshotBeforeBulkAction = ref<{ itemInfo: ItemInfo; index: number }[]>(
  []
)

async function handleUpdate(rowIndex: number, itemName: string) {
  const item = matchedItems.value[rowIndex]

  const status = await fileImportStore.figureOutUpdatedStatus(
    categoryName.value,
    item,
    itemName
  )

  if (status === ItemMatchStatus.EDIT_MATCH) {
    fileImportStore.editBanner = true
    fileImportStore.editItems = {
      externalName: item.externalName,
      previousName: item.internalName as string,
      newName: itemName,
    }
  }

  fileImportStore.setMatchStatus(categoryName.value, rowIndex, itemName, status)
}

function handleIgnore(rowIndex: number) {
  fileImportStore.setMatchToIgnore(categoryName.value, rowIndex)
}

function getCreateStatus(itemName: string): ItemMatchStatus {
  let status = ItemMatchStatus.CREATE

  // If the assigned item name is already a category item.
  if (categoryItems.value.includes(itemName)) {
    status = ItemMatchStatus.MANUAL_MATCH
  }

  return status
}

function handleCreate(rowIndex: number, itemName: string) {
  const status = getCreateStatus(itemName)
  fileImportStore.setMatchStatus(categoryName.value, rowIndex, itemName, status)
}

function snapshotItems() {
  const items: { itemInfo: ItemInfo; index: number }[] = (
    itemMatchInfo.value.get(categoryName.value) ?? []
  ).map((itemInfo, index) => ({ itemInfo, index }))

  const { cloned } = useCloned(items)

  // Deep clone using json functions.
  snapshotBeforeBulkAction.value = cloned.value
}

function handleCreateAll() {
  snapshotItems()
  unmatchedItems.value.forEach((info) => {
    handleCreate(info.index, info.itemInfo.externalName)
  })
}

function handleIgnoreAll() {
  snapshotItems()
  unmatchedItems.value.forEach((info) => {
    handleIgnore(info.index)
  })
}

function handleReset() {
  snapshotBeforeBulkAction.value.forEach((info) => {
    fileImportStore.setMatchStatus(
      categoryName.value,
      info.index,
      info.itemInfo.internalName ?? "",
      info.itemInfo.status
    )
  })
  snapshotBeforeBulkAction.value = []
}

function handleRename(rowIndex: number, newItemName: string) {
  if (
    categoryItems.value.includes(newItemName) ||
    createdItems.value.includes(newItemName)
  ) {
    addSnackbar({
      type: "error",
      message: `Error: unable to rename, ${newItemName} already exists.`,
    })

    return
  }

  fileImportStore.renameNewMatches(categoryName.value, rowIndex, newItemName)
}

const hasSnapshot = computed(() => snapshotBeforeBulkAction.value.length > 0)
</script>

<template>
  <div
    v-if="tabContainsUnmatchedItems || hasSnapshot"
    class="mb-2 border-y border-gray-300 py-4"
  >
    <p
      v-if="tabContainsUnmatchedItems && !hasSnapshot"
      class="flex items-center gap-4"
    >
      <span class="text-sm font-bold">
        Quick action for {{ unmatchedItemsCount }} pending items:
      </span>
      <KButton
        v-tooltip.top.icon="
          'Use names in the file to create new items in the system upon import'
        "
        color="violet"
        variety="secondary"
        @click="handleCreateAll"
      >
        Create and match
      </KButton>
      <KButton
        v-tooltip.top.icon="'Ignore pending items during import'"
        color="violet"
        variety="secondary"
        @click="handleIgnoreAll"
      >
        Ignore
      </KButton>
    </p>

    <p
      v-if="hasSnapshot"
      class="flex items-center gap-2"
    >
      <span class="text-sm font-bold"> Items created and matched </span>
      <KButton
        v-tooltip.top.icon="'Reset items to before bulk action'"
        color="violet"
        variety="secondary"
        @click="handleReset"
      >
        <template #left>
          <RefreshIcon class="size-6" />
        </template>
        Reset
      </KButton>
    </p>
  </div>

  <MatchTable
    :items="matchedItems"
    :allow-create="true"
    :options="dropDownOptions"
    :created-items="createdItems"
    :show-invalid="tab.decorator() === Decorator.INVALID"
    @ignore="handleIgnore"
    @create="handleCreate"
    @update="handleUpdate"
    @rename="handleRename"
  />
</template>
